import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ChatRoom, SystemChatRoom, ResolutionCenterService } from '../../resolution-center.service';
import { GalleryItem, GalleryService } from '@app/gallery/gallery.service';
import { NotificationsService } from '@shared/notifications.service';

@Component({
    selector: 'app-system-chat-room-modal',
    templateUrl: './system-chat-room-modal.component.html',
    styleUrls: ['./system-chat-room-modal.component.scss'],
    standalone: false
})
export class SystemChatRoomModalComponent implements OnInit {
    @Input() chatRoom: ChatRoom | null = null;
    @Output() close = new EventEmitter<void>();
    @Output() updated = new EventEmitter<ChatRoom>();

    public isSaving = false;
    public isClosing = false;

    // Form data
    public formData = {
        reference_name: '',
        name: '',
        description: '',
        image: null as File | null,
        imageUrl: '',
        is_read_only: false,
        show_in_the_app: true
    };
    private existingImageId: number | null = null;

    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private galleryService: GalleryService,
        private notifications: NotificationsService
    ) {}

    ngOnInit(): void {
        if (this.chatRoom) {
            // Populate form with existing chat room data
            // Use reference_name if available, otherwise use custom_name for backward compatibility, or empty string
            const referenceName = this.chatRoom.reference_name || this.chatRoom.custom_name || '';
            
            this.formData = {
                reference_name: referenceName,
                name: this.chatRoom.name || '',
                description: this.chatRoom.description || '',
                image: null,
                imageUrl: this.chatRoom.image?.thumbnail_url || '',
                is_read_only: this.chatRoom.is_read_only || false,
                show_in_the_app: this.chatRoom.show_in_the_app !== undefined ? this.chatRoom.show_in_the_app : true
            };

            const existingImage = this.chatRoom.image as GalleryItem | undefined;
            this.existingImageId = existingImage?.id ?? null;
        }
    }

    onBackdropClick(event: MouseEvent): void {
        if (event.target === event.currentTarget) {
            this.onClose();
        }
    }

    onClose(): void {
        this.isClosing = true;
        setTimeout(() => {
            this.close.emit();
            this.isClosing = false;
        }, 180);
    }

    async onSave(): Promise<void> {
        if (this.isSaving) {
            return;
        }

        // Validate required fields
        if (!this.formData.reference_name?.trim() || !this.formData.name?.trim()) {
            return;
        }

        this.isSaving = true;

        try {
            // Decide which image_id to send:
            // - If user picked a new image file, upload it to /admin/gallery and use returned id
            // - Else, if editing and existing image wasn't removed, reuse its id
            let imageId: number | undefined;

            if (this.formData.image) {
                const uploaded = await this.uploadImageToGallery(this.formData.image);
                if (!uploaded) {
                    this.isSaving = false;
                    return;
                }
                imageId = uploaded.id;
            } else if (this.chatRoom && this.existingImageId) {
                imageId = this.existingImageId;
            }

            // Prepare chat room data
            const chatRoomData: SystemChatRoom = {
                id: this.chatRoom?.id || 0,
                name: this.formData.name,
                reference_name: this.formData.reference_name || undefined,
                description: this.formData.description || undefined,
                image_id: imageId,
                is_read_only: this.formData.is_read_only,
                show_in_the_app: this.formData.show_in_the_app
            };

            const { data, error } = await this.resolutionCenterService.saveSystemChatRoom(chatRoomData);

            if (error || !data) {
                console.error('Error saving system chat room:', error);
                this.notifications.error('Error', 'Something went wrong while saving the system chat room.');
                this.isSaving = false;
                return;
            }

            // Emit the updated chat room
            this.updated.emit(data);

            // Close modal
            this.onClose();
        } catch (error) {
            console.error('Error saving system chat room:', error);
            this.notifications.error('Error', 'Something went wrong while saving the system chat room.');
        } finally {
            this.isSaving = false;
        }
    }

    onImageSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            
            // Validate file type
            if (!file.type.startsWith('image/')) {
                // TODO: Show error message
                return;
            }

            // Validate file size (e.g., max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                // TODO: Show error message
                return;
            }

            // Create preview
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.formData.imageUrl = e.target.result;
            };
            reader.readAsDataURL(file);

            this.formData.image = file;
        }
    }

    onImagePlaceholderClick(input: HTMLInputElement): void {
        input.click();
    }

    onLabelClick(event: Event): void {
        event.stopPropagation();
    }

    onRemoveImage(): void {
        this.formData.image = null;
        this.formData.imageUrl = '';
        this.existingImageId = null;
        // Reset file input
        const fileInput = document.getElementById('system-chat-room-image-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    isFormValid(): boolean {
        return !!(this.formData.reference_name?.trim() && this.formData.name?.trim());
    }

    onUsersCanSendMessagesChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.formData.is_read_only = !target.checked;
    }

    private async uploadImageToGallery(file: File): Promise<GalleryItem | null> {
        const nameParts = file.name.split('.');
        nameParts.pop();
        const baseName = nameParts.join('.') || 'system-chat-room-image';

        const upload$ = await this.galleryService.upload(baseName, file, false, 'image');

        return new Promise<GalleryItem | null>((resolve) => {
            upload$.subscribe(
                res => {
                    if (res.response && res.response.data) {
                        resolve(res.response.data as GalleryItem);
                    }
                },
                error => {
                    const err =
                        error.status >= 500
                            ? error.statusText
                            : (error.error?.errorMessages || []).join('\r\n') || 'Image upload failed';
                    this.notifications.error('Error', `Something went wrong while uploading the image. ${err}`);
                    resolve(null);
                }
            );
        });
    }

    onShowInAppChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.formData.show_in_the_app = target.checked;
    }
}
